import {useT} from "../i18n";
import {useGetAccessToken} from "../client";
import {useQuery} from "react-query";
import {apiUrl} from "../config";
import * as yup from "yup";
import {useCallback, useState} from "react";
import {FormikHelpers} from "formik";
import {
    EntsorgungsdokumentationFilter
} from "../components/entsorgungsdokumentationen/EntsorgungsdokumentationDataTable";
import {useEntsorgungsdokumentationSelection} from "../contexts/EntsorgungsdokumentationSelectionContext";


function useEntsorgungsdokumentation(currentCustomFilter: EntsorgungsdokumentationFilter) {

    const {t: translation} = useT("entsorgungsdokumentation");

    const {
        gewaehlteAbladestellenMaterialien,
        setGewaehlteAbladestellenMaterialien,
        isGewaehlt,
    } = useEntsorgungsdokumentationSelection();

    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    const {refetch} = useCreateEntsorgungsdokumentation(
        currentCustomFilter.baustelleId ?? "",
        currentCustomFilter.startDate ?? "",
        currentCustomFilter.endDate ?? new Date().toISOString().split("T")[0],
        currentCustomFilter.preiseAuswahl ?? false,
        currentCustomFilter.bemerkungenAuswahl ?? false,
        gewaehlteAbladestellenMaterialien
    );

    const validationSchema = yup.object().shape({
        baustelle: yup.object().required(),
        startDatum: yup
            .date()
            .required("Datum ist erforderlich!")
            .max(
                new Date("9999-12-31"),
                "Datum darf nicht über dem 31.12.9999 liegen!"
            ),
        endDatum: yup
            .date()
            .required("Datum ist erforderlich!")
            .max(
                new Date("9999-12-31"),
                "Datum darf nicht über dem 31.12.9999 liegen!"
            ),
        preiseAuswahl: yup.boolean().nullable(),
        bemerkungenAuswahl: yup.boolean().nullable(),
        gewaehlteAbladestellenMaterialien: yup.array(),
    });

    function useCreateEntsorgungsdokumentation(
        baustellenId: string,
        startDate: string,
        endDate: string,
        preiseAuswahl: boolean,
        bemerkungenAuswahl: boolean,
        gewaehlteAbladestellenMaterialien: string[]
    ) {
        const getAccessToken = useGetAccessToken();

        return useQuery(
            [
                "create-entsorgungsdokumentation",
                baustellenId,
                startDate,
                endDate,
                preiseAuswahl,
                bemerkungenAuswahl,
                gewaehlteAbladestellenMaterialien,
            ],
            async () => {
                const response = await fetch(
                    `${apiUrl}/entsorgungsdokumentation/download`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: `Bearer ${await getAccessToken()}`,
                        },
                        body: JSON.stringify({
                            baustelleId: baustellenId,
                            startDatum: startDate,
                            endDatum: endDate,
                            preiseAuswahl: preiseAuswahl,
                            bemerkungenAuswahl: bemerkungenAuswahl,
                            gewaehlteAbladestellenMaterialien:
                            gewaehlteAbladestellenMaterialien,
                        }),
                    }
                );

                if (!response.ok) {
                    throw new Error(`Download fehlgeschlagen (${response.status})`);
                }

                const blob = await response.blob();
                return window.URL.createObjectURL(blob);
            },
            {
                enabled: false,
                suspense: false,
                cacheTime: 0,
            }
        );
    }

    const handlePreviewClick = async () => {
        if (currentCustomFilter.baustelleId && currentCustomFilter.startDate && currentCustomFilter.endDate) {
            setIsLoadingPreview(true);
            const result = await refetch();
            setIsLoadingPreview(false);
            if (result.data) {
                window.open(result.data, "_blank");
            }
        }
    };

    const getAccessToken = useGetAccessToken();
    const onSubmitGeneriereEntsorgungsdoku = useCallback(
        async (
            _values: any,
            formikHelpers: FormikHelpers<any>
        ) => {
            const {setSubmitting, setStatus} = formikHelpers;
            setSubmitting(true);
            try {
                const baustellenId = currentCustomFilter.baustelleId ?? "";
                const response = await fetch(
                    `${apiUrl}/entsorgungsdokumentation/download`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: `Bearer ${await getAccessToken()}`,
                        },
                        body: JSON.stringify({
                            baustelleId: baustellenId,
                            startDatum: currentCustomFilter.startDate ?? "",
                            endDatum: currentCustomFilter.endDate ?? new Date().toISOString().split("T")[0],
                            preiseAuswahl: currentCustomFilter.preiseAuswahl ?? false,
                            bemerkungenAuswahl: currentCustomFilter.bemerkungenAuswahl ?? false,
                            gewaehlteAbladestellenMaterialien:
                            gewaehlteAbladestellenMaterialien,
                        }),
                    }
                );

                const blob = await response.blob();
                const link = document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.download = "entsorgungsdokumentation.pdf";
                link.click();
            } catch (error: any) {
                setStatus(error.message);
            } finally {
                setSubmitting(false);
            }
        },
        [getAccessToken, gewaehlteAbladestellenMaterialien, currentCustomFilter]
    );

    return {
        validationSchema,
        isLoadingPreview,
        isGewaehlt,
        handlePreviewClick,
        onSubmitGeneriereEntsorgungsdoku,
        translation,
    };
}

export default useEntsorgungsdokumentation;
